import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import API_BASE_URL, { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface Chama {
  id: string;
  name: string;
  description?: string;
  category?: string;
  type?: string;
  contributionAmount?: number;
  contributionFrequency?: string;
  maxMembers?: number;
  currentMembers?: number;
  totalFunds?: number;
}

const ChamaContribute: React.FC = () => {
  const { chamaId } = useParams<{ chamaId: string }>();
  const colors = getThemeColors('dark');
  const [chama, setChama] = useState<Chama | null>(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('wallet');
  const [walletBalance, setWalletBalance] = useState(0);
  const [contributionType, setContributionType] = useState('regular');
  const [userRole, setUserRole] = useState('member');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [chamaMembers, setChamaMembers] = useState<any[]>([]);
  const [selectedContributor, setSelectedContributor] = useState<any>(null);
  const [currentRecipient, setCurrentRecipient] = useState<any>(null);
  const [contributionStatus, setContributionStatus] = useState<any>(null);
  const [statusCheckInterval, setStatusCheckInterval] = useState<NodeJS.Timeout | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    if (chamaId) {
      loadChamaData();
      loadWalletBalance();
      loadUserRole();
      loadChamaMembers();

      // For merry-go-round contributions, fetch current recipient info
      if (contributionType === 'merry-go-round') {
        loadCurrentRecipient();
        startStatusChecking();
      }
    }

    return () => {
      stopStatusChecking();
    };
  }, [chamaId, contributionType]);

  const loadUserRole = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        console.error('❌ No auth token found');
        setUserRole('member');
        return;
      }

      // Get current user ID from token
      let userId;
      try {
        const tokenPayload = JSON.parse(atob(authToken.split('.')[1]));
        userId = tokenPayload.userId;
      } catch (tokenError) {
        console.error('❌ Invalid auth token format:', tokenError);
        setUserRole('member');
        return;
      }

      console.log('🔍 Loading user role for chama:', chamaId, 'user:', userId);

      const response = await fetch(`${API_BASE_URL}/api/v1/chamas/${chamaId}/members/${userId}/role`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUserRole(data.data?.role || 'member');
          console.log('✅ User role loaded:', data.data?.role);
        } else {
          console.error('❌ Failed to load user role - API returned success=false:', data);
          setUserRole('member');
        }
      } else {
        console.error('❌ Failed to load user role - HTTP status:', response.status, response.statusText);
        setUserRole('member');
      }
    } catch (error) {
      console.error('❌ Failed to load user role:', error);
      setUserRole('member');
    }
  };

  const loadChamaMembers = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      // For merry-go-round contributions, load members from the merry-go-round circle
      if (contributionType === 'merry-go-round') {
        // This would need roundId parameter - for now, load regular members
        const response = await fetch(`${API_BASE_URL}/api/v1/contributions/chamas/${chamaId}/members`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setChamaMembers(data.data || []);
          }
        }
      } else {
        // For regular contributions, load all chama members (treasurer only)
        if (userRole === 'treasurer' || userRole === 'chairperson') {
          const response = await fetch(`${API_BASE_URL}/api/v1/contributions/chamas/${chamaId}/members`, {
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setChamaMembers(data.data || []);
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to load chama members:', error);
    }
  };

  const loadCurrentRecipient = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(API_ENDPOINTS.MERRY_GO_ROUND_CONTRIBUTION_STATUS(chamaId), {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const statusData = data.data;
          setContributionStatus(statusData);

          if (statusData.currentRecipient) {
            setCurrentRecipient({
              id: statusData.currentRecipient.id,
              firstName: statusData.currentRecipient.name.split(' ')[0] || '',
              lastName: statusData.currentRecipient.name.split(' ').slice(1).join(' ') || '',
              fullName: statusData.currentRecipient.name,
              position: statusData.currentRound,
              amountPerRound: statusData.amountPerRound
            });

            // Set the contribution amount to the exact required amount
            if (statusData.amountPerRound && statusData.amountPerRound > 0) {
              setAmount(statusData.amountPerRound.toString());
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to load current recipient:', error);
    }
  };

  const startStatusChecking = () => {
    if (contributionType === 'merry-go-round' && !statusCheckInterval && chamaId) {
      // Check immediately
      checkContributionStatus();

      // Set up interval for periodic checks (every 15 seconds)
      const interval = setInterval(() => {
        checkContributionStatus();
      }, 15000);

      setStatusCheckInterval(interval);
    }
  };

  const stopStatusChecking = () => {
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
      setStatusCheckInterval(null);
    }
  };

  const checkContributionStatus = async () => {
    if (contributionType !== 'merry-go-round' || !chamaId || isCheckingStatus) {
      return;
    }

    setIsCheckingStatus(true);

    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(API_ENDPOINTS.MERRY_GO_ROUND_CONTRIBUTION_STATUS(chamaId), {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const statusData = data.data;
          setContributionStatus(statusData);

          // Update current recipient if it changed
          if (statusData.currentRecipient && statusData.currentRecipient.id !== currentRecipient?.id) {
            setCurrentRecipient({
              id: statusData.currentRecipient.id,
              firstName: statusData.currentRecipient.name.split(' ')[0] || '',
              lastName: statusData.currentRecipient.name.split(' ').slice(1).join(' ') || '',
              fullName: statusData.currentRecipient.name,
              position: statusData.currentRound,
              amountPerRound: statusData.amountPerRound
            });

            // Update amount if it changed
            if (statusData.amountPerRound && statusData.amountPerRound !== parseFloat(amount)) {
              setAmount(statusData.amountPerRound.toString());
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to check contribution status:', error);
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const loadChamaData = async () => {
    if (!chamaId) return;

    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        console.error('❌ ChamaContribute: No auth token found');
        alert('Authentication required. Please log in again.');
        return;
      }

      console.log('🔍 Loading chama data for ID:', chamaId);

      const response = await fetch(`${API_BASE_URL}/api/v1/chamas/${chamaId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ ChamaContribute: Failed to load chama details:', errorData);
        alert(`Failed to load chama details: ${errorData.error || 'Unknown error'}`);
        return;
      }

      const data = await response.json();
      if (data.success && data.data) {
        setChama(data.data);
        console.log('✅ Chama data loaded:', data.data.name);
        // Only set amount for regular contributions, not for merry-go-round
        if (contributionType !== 'merry-go-round') {
          setAmount(data.data.contributionAmount?.toString() || '');
        }
      } else {
        console.error('❌ ChamaContribute: Invalid chama data structure:', data);
        alert('Invalid chama data received from server');
      }
    } catch (error) {
      console.error('❌ ChamaContribute: Error loading chama details:', error);
      alert('Network error: Unable to connect to the server. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadWalletBalance = async () => {
    try {
      console.log('🔄 Loading wallet balance...');
      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        console.error('❌ ChamaContribute: No auth token found');
        setWalletBalance(0);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/wallets/balance`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const balance = data.data.balance || 0;
          setWalletBalance(balance);
          console.log('✅ Wallet balance loaded:', balance);
        } else {
          console.log('⚠️ Wallet balance response not successful:', data);
          setWalletBalance(0);
        }
      } else {
        console.log('⚠️ Wallet balance request failed:', response.status, response.statusText);
        const errorText = await response.text().catch(() => 'Unknown error');
        console.log('⚠️ Error details:', errorText);
        setWalletBalance(0);
      }
    } catch (error) {
      console.error('❌ Failed to load wallet balance:', error);
      setWalletBalance(0);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handleContribute = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert('Please enter a valid contribution amount');
      return;
    }

    if (!chama) {
      alert('Chama details not loaded. Please try again.');
      return;
    }

    // For merry-go-round, check real-time contribution status
    if (contributionType === 'merry-go-round') {
      await checkContributionStatus();

      if (contributionStatus?.hasContributed) {
        alert('You have already contributed to this merry-go-round round. Each member can only contribute once per round.');
        return;
      }

      if (!contributionStatus?.isParticipant) {
        alert('You are not a participant in this merry-go-round circle.');
        return;
      }
    }

    // Validate payment method
    if (!validatePaymentMethod(paymentMethod)) {
      return;
    }

    // Validate amount for merry-go-round
    if (!validateContributionAmount(amount)) {
      return;
    }

    // Validate payment method specific requirements
    if (paymentMethod === 'wallet') {
      if (walletBalance <= 0) {
        alert('Your VaultKe wallet balance is KES 0.00. Please deposit money into your wallet before making a contribution.');
        return;
      }

      if (parseFloat(amount) > walletBalance) {
        const shortfall = parseFloat(amount) - walletBalance;
        const message = contributionType === 'merry-go-round'
          ? `Your VaultKe wallet balance is KES ${formatCurrency(walletBalance)}.\n\nYou need KES ${formatCurrency(shortfall)} more to make this merry-go-round contribution of KES ${formatCurrency(parseFloat(amount))}.\n\nMerry-go-round contributions require the exact amount and cannot be reduced.`
          : `Your VaultKe wallet balance is KES ${formatCurrency(walletBalance)}.\n\nYou need KES ${formatCurrency(shortfall)} more to make this contribution.`;

        alert(`Insufficient Wallet Balance\n\n${message}`);
        return;
      }
    } else if (paymentMethod === 'cash' || paymentMethod === 'cheque') {
      if (!selectedContributor) {
        alert(`Please select the member who made this ${paymentMethod} contribution.`);
        return;
      }
    }

    // Show confirmation modal
    setShowPaymentModal(true);
  };

  const confirmContribution = async () => {
    try {
      setLoading(true);
      setShowPaymentModal(false);

      const cleanChamaId = chamaId!;

      if (paymentMethod === 'mpesa') {
        await handleMpesaContribution(cleanChamaId);
      } else if (paymentMethod === 'cash' || paymentMethod === 'cheque') {
        await handleCashContribution(cleanChamaId);
      } else {
        await handleWalletContribution(cleanChamaId);
      }
    } catch (error) {
      console.error('Contribution failed:', error);
      alert(error instanceof Error ? error.message : 'An error occurred while processing your contribution');
    } finally {
      setLoading(false);
    }
  };

  const handleWalletContribution = async (cleanChamaId: string) => {
    const contributionData = {
      chamaId: cleanChamaId,
      amount: parseFloat(amount),
      description: description || getDefaultDescription(),
      type: contributionType,
      paymentMethod: 'wallet',
      isAnonymous: chama?.category === 'contribution' ? isAnonymous : false,
    };

    const response = await fetch(`${API_BASE_URL}/api/v1/contributions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contributionData),
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        alert('Contribution successful!');
        await loadWalletBalance();
        // Reset form
        setAmount('');
        setDescription('');
        setIsAnonymous(false);
      } else {
        throw new Error(data.error || 'Wallet contribution failed');
      }
    } else {
      throw new Error('Wallet contribution failed');
    }
  };

  const handleMpesaContribution = async (cleanChamaId: string) => {
    // Format phone number
    const userPhone = JSON.parse(atob(localStorage.getItem('authToken')!.split('.')[1])).phone || '';
    let formattedPhone = userPhone.replace(/\s+/g, '');
    if (formattedPhone.startsWith('0')) {
      formattedPhone = '254' + formattedPhone.substring(1);
    }

    const accountReference = `CHAMA-${cleanChamaId.substring(0, 8)}`;
    const transactionDesc = description || getDefaultDescription();

    const mpesaResponse = await fetch(API_ENDPOINTS.MPESA_STK_PUSH, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phoneNumber: formattedPhone,
        amount: parseFloat(amount),
        accountReference,
        transactionDesc,
      }),
    });

    if (mpesaResponse.ok) {
      alert('M-Pesa STK push sent! Please check your phone and enter your PIN to complete the payment.');
    } else {
      throw new Error('Failed to initiate M-Pesa payment');
    }
  };

  const handleCashContribution = async (cleanChamaId: string) => {
    if (!selectedContributor) {
      throw new Error('Please select the member who made this contribution');
    }

    const contributionData = {
      chamaId: cleanChamaId,
      amount: parseFloat(amount),
      description: description || getDefaultDescription(),
      type: contributionType || 'regular',
      paymentMethod: paymentMethod,
      contributorId: selectedContributor.id,
      cashType: paymentMethod,
      isAnonymous: false,
    };

    const response = await fetch(`${API_BASE_URL}/api/v1/contributions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contributionData),
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        alert('Contribution recorded successfully');
      } else {
        throw new Error(data.error || 'Failed to record cash contribution');
      }
    } else {
      throw new Error('Failed to record cash contribution');
    }
  };

  const getDefaultDescription = () => {
    switch (contributionType) {
      case 'merry-go-round':
        return 'Merry-Go-Round contribution';
      case 'welfare':
        return 'Welfare contribution';
      case 'loan':
        return 'Loan contribution';
      case 'emergency':
        return 'Emergency contribution';
      default:
        return 'Chama contribution';
    }
  };

  const validatePaymentMethod = (method: string) => {
    if (contributionType === 'merry-go-round') {
      if (isAnonymous) {
        alert('Anonymous contributions are not allowed for merry-go-round. All contributions must be traceable to maintain fairness.');
        return false;
      }

      if (method === 'cheque') {
        alert('Cheque payments are not allowed for merry-go-round contributions. Only wallet and M-Pesa payments are permitted.');
        return false;
      }

      if (method !== 'wallet' && method !== 'mpesa') {
        alert('Only wallet and M-Pesa payments are allowed for merry-go-round contributions.');
        return false;
      }
    }
    return true;
  };

  const validateContributionAmount = (amount: string) => {
    if (contributionType === 'merry-go-round') {
      const expectedAmount = contributionStatus?.amountPerRound || currentRecipient?.amountPerRound || 0;

      if (expectedAmount > 0) {
        const inputAmount = parseFloat(amount);

        if (inputAmount !== expectedAmount) {
          alert(`Merry-go-round contributions must be exactly ${expectedAmount} KES. You cannot contribute more or less than the required amount.`);
          return false;
        }
      }
    }
    return true;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading contribution page...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title={`${chama?.name || 'Chama'} - Contribute`}
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Chama Info */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div style={styles.cardHeader}>
              <div style={styles.chamaAvatar}>
                <span style={{ fontSize: '24px' }}>👥</span>
              </div>
              <div style={styles.chamaInfo}>
                <h3 style={{ ...styles.chamaName, color: colors.text }}>{chama?.name}</h3>
                <p style={{ ...styles.chamaDescription, color: colors.textSecondary }}>
                  {chama?.description || 'No description available'}
                </p>
              </div>
            </div>
          </div>

          {/* Contribution Form */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.cardTitle, color: colors.text }}>Make Contribution</h3>

            {/* Contribution Type */}
            <div style={styles.formGroup}>
              <label style={{ ...styles.label, color: colors.text }}>Contribution Type</label>
              <select
                value={contributionType}
                onChange={(e) => setContributionType(e.target.value)}
                style={{ ...styles.select, backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}
              >
                <option value="regular">Regular Contribution</option>
                <option value="special">Special Contribution</option>
                <option value="welfare">Welfare Fund</option>
                <option value="loan">Loan Fund</option>
              </select>
            </div>

            {/* Amount */}
            <div style={styles.formGroup}>
              <label style={{ ...styles.label, color: colors.text }}>Amount (KES)</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                style={{ ...styles.input, backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}
              />
            </div>

            {/* Payment Method */}
            <div style={styles.formGroup}>
              <label style={{ ...styles.label, color: colors.text }}>Payment Method</label>
              <div style={styles.paymentMethods}>
                <div
                  style={{
                    ...styles.paymentMethod,
                    backgroundColor: paymentMethod === 'wallet' ? colors.primary + '20' : colors.surface,
                    border: `2px solid ${paymentMethod === 'wallet' ? colors.primary : colors.border}`,
                  }}
                  onClick={() => setPaymentMethod('wallet')}
                >
                  <span style={{ fontSize: '24px', marginBottom: '8px' }}>💰</span>
                  <div style={{ ...styles.paymentMethodText, color: colors.text }}>VaultKe Wallet</div>
                  <div style={{ ...styles.balanceText, color: colors.textSecondary }}>
                    Balance: {formatCurrency(walletBalance)}
                  </div>
                </div>

                <div
                  style={{
                    ...styles.paymentMethod,
                    backgroundColor: paymentMethod === 'mpesa' ? colors.success + '20' : colors.surface,
                    border: `2px solid ${paymentMethod === 'mpesa' ? colors.success : colors.border}`,
                  }}
                  onClick={() => setPaymentMethod('mpesa')}
                >
                  <span style={{ fontSize: '24px', marginBottom: '8px' }}>📱</span>
                  <div style={{ ...styles.paymentMethodText, color: colors.text }}>M-Pesa</div>
                  <div style={{ ...styles.balanceText, color: colors.textSecondary }}>
                    Mobile Money
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div style={styles.formGroup}>
              <label style={{ ...styles.label, color: colors.text }}>Description (Optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add a note for this contribution..."
                rows={3}
                style={{ ...styles.textarea, backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}
              />
            </div>

            {/* Validation Messages */}
            {paymentMethod === 'wallet' && amount && parseFloat(amount) > walletBalance && (
              <div style={{ ...styles.validationMessage, backgroundColor: colors.error + '20', border: `1px solid ${colors.error}` }}>
                <span style={{ color: colors.error }}>
                  ⚠️ Insufficient balance. You need {formatCurrency(parseFloat(amount) - walletBalance)} more.
                </span>
              </div>
            )}

            {/* Contribute Button */}
            <button
              onClick={handleContribute}
              disabled={!amount || parseFloat(amount) <= 0 || (paymentMethod === 'wallet' && parseFloat(amount) > walletBalance)}
              style={{
                ...styles.contributeButton,
                backgroundColor: (!amount || parseFloat(amount) <= 0 || (paymentMethod === 'wallet' && parseFloat(amount) > walletBalance))
                  ? colors.textSecondary
                  : colors.primary,
                cursor: (!amount || parseFloat(amount) <= 0 || (paymentMethod === 'wallet' && parseFloat(amount) > walletBalance))
                  ? 'not-allowed'
                  : 'pointer',
              }}
            >
              {paymentMethod === 'wallet' ? 'Contribute from Wallet' : 'Pay with M-Pesa'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80,
    paddingBottom: 80,
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxxl,
  },
  card: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    marginBottom: spacing.md,
    ...getShadowStyle('sm'),
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  chamaAvatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: 'rgba(33, 150, 243, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  chamaInfo: {
    flex: 1,
  },
  chamaName: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    margin: 0,
    marginBottom: spacing.xs,
  },
  chamaDescription: {
    fontSize: typography.fontSize.sm,
    margin: 0,
    lineHeight: 1.4,
  },
  cardTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    margin: 0,
    marginBottom: spacing.md,
  },
  formGroup: {
    marginBottom: spacing.lg,
  },
  label: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
    display: 'block',
  },
  input: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    fontSize: typography.fontSize.base,
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    fontSize: typography.fontSize.base,
    outline: 'none',
  },
  textarea: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    fontSize: typography.fontSize.base,
    outline: 'none',
    resize: 'vertical',
    minHeight: 80,
  },
  paymentMethods: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.md,
  },
  paymentMethod: {
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.2s ease',
  },
  paymentMethodText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.xs,
  },
  balanceText: {
    fontSize: typography.fontSize.sm,
  },
  validationMessage: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    marginBottom: spacing.md,
  },
  contributeButton: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginTop: spacing.md,
  },
};

export default ChamaContribute;